import React, { useState } from 'react';
import { MapPin, Edit2, Power, DollarSign, Plus, Trash2 } from 'lucide-react';
import { getProgramStatusColor } from '../../../utils/statusColors';
import ProgramEditor from './ProgramEditor';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CreateProgramModal = ({ onCreate, onClose }) => {
  const [f, setF] = useState({ id: '', country_name: '', program_city: '',
    program_title: '', status: 'active' });
  const canSubmit = f.id.trim() && f.country_name.trim() && f.program_title.trim();
  const submit = async () => {
    if (!canSubmit) return;
    await onCreate({
      ...f,
      id: f.id.trim().toLowerCase().replace(/[^a-z0-9-]+/g, '-'),
    });
    onClose();
  };
  return (
    <div className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4"
         onClick={onClose} data-testid="create-program-modal">
      <div className="bg-white rounded-lg p-6 max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-heading font-bold text-lg mb-4">New Country Program</h3>
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-[#4B5563] mb-1">Program ID (URL slug)</label>
            <input type="text" value={f.id} onChange={(e) => setF({ ...f, id: e.target.value })}
              placeholder="e.g. colombia-choco"
              className="w-full px-3 py-2 border border-[#E5E7EB] rounded-sm text-sm"
              data-testid="new-program-id" />
            <p className="text-[10px] text-[#9CA3AF] mt-1">
              Used in the URL: /international/{f.id || 'your-slug'}. Lowercase letters, numbers, hyphens.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <input type="text" value={f.country_name}
              onChange={(e) => setF({ ...f, country_name: e.target.value })}
              placeholder="Country (e.g. Colombia)"
              className="w-full px-3 py-2 border border-[#E5E7EB] rounded-sm text-sm"
              data-testid="new-program-country" />
            <input type="text" value={f.program_city}
              onChange={(e) => setF({ ...f, program_city: e.target.value })}
              placeholder="City / Region (e.g. Chocó)"
              className="w-full px-3 py-2 border border-[#E5E7EB] rounded-sm text-sm"
              data-testid="new-program-city" />
          </div>
          <input type="text" value={f.program_title}
            onChange={(e) => setF({ ...f, program_title: e.target.value })}
            placeholder="Program title (e.g. Chocó Emergency Response)"
            className="w-full px-3 py-2 border border-[#E5E7EB] rounded-sm text-sm"
            data-testid="new-program-title" />
          <select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })}
            className="w-full px-3 py-2 border border-[#E5E7EB] rounded-sm text-sm">
            <option value="active">Active</option>
            <option value="pending">Pending</option>
            <option value="coming_soon">Coming Soon</option>
            <option value="paused">Paused (Hidden)</option>
          </select>
        </div>
        <div className="flex gap-2 mt-5">
          <button onClick={submit} disabled={!canSubmit}
            className={`btn-primary ${!canSubmit ? 'opacity-50 cursor-not-allowed' : ''}`}
            data-testid="submit-new-program">
            Create program
          </button>
          <button onClick={onClose} className="btn-secondary">Cancel</button>
        </div>
      </div>
    </div>
  );
};

export const AdminProgramsTab = ({ ctx }) => {
  const {
    countryPrograms, editingProgram, setEditingProgram,
    updateProgram, createProgram, deleteProgram,
    fetchProgramDonations, setActiveTab
  } = ctx;
  const [showCreate, setShowCreate] = useState(false);

  const quickToggleStatus = async (programId, currentStatus) => {
    const nextStatus = currentStatus === 'active' ? 'paused' : 'active';
    await updateProgram(programId, { status: nextStatus });
  };

  const quickToggleDonations = async (programId, currentEnabled) => {
    await updateProgram(programId, { donations_enabled: !currentEnabled });
  };

  if (editingProgram) {
    return (
      <ProgramEditor
        program={editingProgram}
        onSave={(data) => updateProgram(editingProgram.id, data)}
        onCancel={() => setEditingProgram(null)}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-heading font-bold text-lg">Country Programs</h3>
          <p className="text-xs text-[#4B5563]">Each program has its own editor — global content stays available too.</p>
        </div>
        <button onClick={() => setShowCreate(true)}
          className="btn-primary text-sm flex items-center gap-1"
          data-testid="new-country-program-btn">
          <Plus className="w-4 h-4" /> New country program
        </button>
      </div>

      {showCreate && (
        <CreateProgramModal
          onCreate={createProgram}
          onClose={() => setShowCreate(false)}
        />
      )}

      {countryPrograms.map((program) => {
        const progress = program.monthly_goal > 0
          ? Math.min((program.amount_raised / program.monthly_goal) * 100, 100) : 0;
        return (
          <div key={program.id} className="bg-white border border-[#E5E7EB] rounded-lg p-5" data-testid={`admin-program-${program.id}`}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-[#BA1826]" />
                  <h4 className="font-heading font-bold">{program.country_name} — {program.program_city || 'TBD'}</h4>
                </div>
                <p className="text-sm text-[#4B5563]">{program.program_title}</p>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-xs px-2 py-1 rounded ${getProgramStatusColor(program.status)}`}>{program.status}</span>
                <button onClick={() => quickToggleDonations(program.id, program.donations_enabled !== false)}
                  className={`text-xs px-2 py-1 rounded flex items-center gap-1 ${program.donations_enabled !== false ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}
                  title={program.donations_enabled !== false ? 'Click to turn OFF donations' : 'Click to turn ON donations'}
                  data-testid={`toggle-donations-${program.id}`}>
                  <DollarSign className="w-3 h-3" />
                  {program.donations_enabled !== false ? 'Donations ON' : 'Donations OFF'}
                </button>
                <button onClick={() => quickToggleStatus(program.id, program.status)}
                  className={`text-xs px-2 py-1 rounded flex items-center gap-1 ${program.status === 'active' ? 'bg-green-100 text-green-700 hover:bg-red-100 hover:text-red-700' : 'bg-gray-100 text-gray-500 hover:bg-green-100 hover:text-green-700'}`}
                  title={program.status === 'active' ? 'Click to pause' : 'Click to activate'}
                  data-testid={`toggle-status-${program.id}`}>
                  <Power className="w-3 h-3" />
                  {program.status === 'active' ? 'Active' : 'Paused'}
                </button>
                <button onClick={() => setEditingProgram(program)} className="text-[#BA1826] hover:underline text-sm" data-testid={`edit-program-${program.id}`} aria-label={`Edit ${program.country_name}`}>
                  <Edit2 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => deleteProgram(program.id, `${program.country_name} — ${program.program_title}`)}
                  className="text-[#B91C1C] hover:text-red-700"
                  data-testid={`delete-program-${program.id}`}
                  aria-label={`Delete ${program.country_name}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="grid grid-cols-4 gap-3 mb-3 text-sm">
              <div>
                <p className="text-xs text-[#4B5563]">Monthly Goal</p>
                <p className="font-bold">${program.monthly_goal.toLocaleString()}</p>
              </div>
              <div>
                <p className="text-xs text-[#4B5563]">Raised</p>
                <p className="font-bold text-[#BA1826]">${program.amount_raised.toLocaleString()}</p>
              </div>
              <div>
                <p className="text-xs text-[#4B5563]">Target</p>
                <p className="font-bold">{program.families_target} families</p>
              </div>
              <div>
                <p className="text-xs text-[#4B5563]">Cost/Family</p>
                <p className="font-bold">${program.cost_per_family}</p>
              </div>
            </div>

            <div className="relative h-3 bg-[#F1F5F9] rounded-full overflow-hidden mb-2">
              <div className="absolute inset-y-0 left-0 bg-[#BA1826] rounded-full" style={{ width: `${progress}%` }}></div>
            </div>
            <p className="text-xs text-[#4B5563]">{progress.toFixed(1)}% of monthly goal</p>

            <button
              onClick={() => { fetchProgramDonations(program.id); setActiveTab('program_donations_' + program.id); }}
              className="text-xs text-[#BA1826] hover:underline mt-2"
            >
              View program donations
            </button>
          </div>
        );
      })}
    </div>
  );
};

export const ProgramDonationsTab = ({ ctx }) => {
  const { activeTab, countryPrograms, programDonations, setActiveTab } = ctx;

  return (
    <div className="space-y-4">
      <button onClick={() => setActiveTab('programs')} className="text-sm text-[#BA1826] hover:underline mb-4">
        Back to Programs
      </button>
      <h3 className="font-heading font-semibold text-lg">
        Donations for {countryPrograms.find(p => 'program_donations_' + p.id === activeTab)?.program_title}
      </h3>
      <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-[#F8F9FA]">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-[#4B5563]">Date</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-[#4B5563]">Donor</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-[#4B5563]">Amount</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-[#4B5563]">Status</th>
            </tr>
          </thead>
          <tbody>
            {programDonations.length === 0 ? (
              <tr><td colSpan="4" className="px-4 py-8 text-center text-sm text-[#4B5563]">No donations yet for this program.</td></tr>
            ) : programDonations.map((d) => (
              <tr key={d.id} className="border-t border-[#E5E7EB]">
                <td className="px-4 py-3 text-sm">{new Date(d.created_at).toLocaleDateString()}</td>
                <td className="px-4 py-3 text-sm">{d.donor_name || 'Anonymous'}</td>
                <td className="px-4 py-3 text-sm font-semibold">${d.amount}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-1 rounded ${d.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>{d.status}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
